// ═══════════════════════════════════════════════════════════
// UTILITIES & HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════

import { Vector2 } from './types.js';

export class Utils {
    // Easing functions for smooth animations
    static easeOutCubic(t: number): number {
        return 1 - Math.pow(1 - t, 3);
    }

    static easeInCubic(t: number): number {
        return t * t * t;
    }

    static easeOutElastic(t: number): number {
        const c4 = (2 * Math.PI) / 3;
        return t === 0
            ? 0
            : t === 1
            ? 1
            : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
    }

    static easeOutBack(t: number): number {
        const c1 = 1.70158;
        const c3 = c1 + 1;
        return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    }

    // Linear interpolation
    static lerp(start: number, end: number, t: number): number {
        return start + (end - start) * t;
    }

    // Clamp value between min and max
    static clamp(value: number, min: number, max: number): number {
        return Math.max(min, Math.min(max, value));
    }

    // Distance between two points
    static distance(p1: Vector2, p2: Vector2): number {
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        return Math.sqrt(dx * dx + dy * dy);
    }

    // Normalize vector
    static normalize(v: Vector2): Vector2 {
        const length = Math.sqrt(v.x * v.x + v.y * v.y);
        if (length === 0) return { x: 0, y: 0 };
        return {
            x: v.x / length,
            y: v.y / length
        };
    }

    // Random number between min and max
    static random(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    // Random integer between min and max (inclusive)
    static randomInt(min: number, max: number): number {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    // Random choice from array
    static randomChoice<T>(array: T[]): T {
        return array[Math.floor(Math.random() * array.length)];
    }

    // HSL to RGB conversion for particle colors
    static hslToRgb(h: number, s: number, l: number): string {
        const a = s * Math.min(l, 1 - l);
        const f = (n: number) => {
            const k = (n + h / 30) % 12;
            return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        };
        const r = Math.round(f(0) * 255);
        const g = Math.round(f(8) * 255);
        const b = Math.round(f(4) * 255);
        return `rgb(${r}, ${g}, ${b})`;
    }

    // Format time as MM:SS
    static formatTime(seconds: number): string {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    // Format score with leading zeros
    static formatScore(score: number): string {
        return score.toString().padStart(6, '0');
    }

    // Shake amount calculation
    static shakeAmount(intensity: number, decay: number): Vector2 {
        const angle = Math.random() * Math.PI * 2;
        const distance = intensity * decay;
        return {
            x: Math.cos(angle) * distance,
            y: Math.sin(angle) * distance
        };
    }
}
